import { useState } from 'react'
import { useRecordBookingPayment } from '@/entities/booking'
import { newIdempotencyKey } from '@/shared/api'
import type { RecordBookingPayment } from '@/shared/api'
import { rubleInputToAmount } from '@/shared/lib/format'
import { Button, Dialog, Input, Select, Textarea } from '@/shared/ui'
import { paymentFailure } from '../lib/payment-failure'
import s from './BookingPaymentDialog.module.css'

/**
 * ЧТО ИМЕННО ЗАПИСЫВАЮТ — одним выбором вместо двух. У домена это ДВА поля: вид (`payment` или
 * `deposit`) и направление (`income`, `refund`, `fine`). Шесть их сочетаний менеджеру не нужны и
 * не читаются: «вид: залог, направление: удержание» — это язык таблицы, а не кассы.
 *
 * Поэтому на экране один список из пяти осмысленных случаев, а пара полей собирается здесь. Шестое
 * сочетание (`payment:fine`, штраф вне залога) не предлагаем: штраф удерживают из залога, а не
 * начисляют отдельно, — появится случай, добавим строку.
 *
 * ⚠️ Подписи наши, и это законно: они называют не данные сервера, а ВЫБОР в нашей форме. Слова
 * ленты (`title`, `stateLabel`) по-прежнему приходят с сервера — их мы не сочиняем.
 */
const KINDS: ReadonlyArray<{
  value: string
  label: string
  kind: RecordBookingPayment['kind']
  direction: RecordBookingPayment['direction']
}> = [
  { value: 'payment', label: 'Оплата проживания', kind: 'payment', direction: 'income' },
  { value: 'deposit', label: 'Залог', kind: 'deposit', direction: 'income' },
  { value: 'refund', label: 'Возврат гостю', kind: 'payment', direction: 'refund' },
  { value: 'deposit-refund', label: 'Возврат залога', kind: 'deposit', direction: 'refund' },
  { value: 'deposit-fine', label: 'Удержано из залога', kind: 'deposit', direction: 'fine' },
]

/*
 * Первый случай списка, он же значение по умолчанию. Отдельной константой, а не `KINDS[0]`: при
 * строгих индексах массив отдаёт `T | undefined`, и на каждом обращении пришлось бы либо ставить
 * `!`, либо проверять невозможное. Здесь же видно, ЧТО именно предложено первым.
 */
const FALLBACK_KIND = {
  value: 'payment',
  label: 'Оплата проживания',
  kind: 'payment',
  direction: 'income',
} as const satisfies (typeof KINDS)[number]

/**
 * «Записать платёж» — ручная запись менеджера: наличные на заселении, залог, возврат.
 *
 * ⚠️ ЭТО ДЕНЬГИ КАССЫ, А НЕ СЛОВА ПЛОЩАДКИ. Предоплата и залог, о которых сообщила площадка, живут
 * в секции «Данные источника» и правке не подлежат — там мы пересказываем чужое утверждение, и
 * источник истины по нему площадка. Здесь — то, что компания приняла сама.
 *
 * ⚠️ ЧЕМ ПЛАТИЛИ — СВОИМ ПОЛЕМ, И СПРАВОЧНИК ДЛЯ НЕГО НЕ НУЖЕН. Машинный `payment_system` (`cash`,
 * `card`, `moneta`…) подписей действительно не имеет, но у домена РЯДОМ лежит `invoice_name` —
 * «имя счёта агентства («Наличные», «Карта»)», строка свободная и человеческая, и она есть на
 * записи, на чтении и во входе создания. Сочинять словарь не пришлось: слово называет сам менеджер.
 *
 * Складывая способ оплаты в примечание, мы теряли его на обеих сторонах — сервер не отличил бы его
 * от произвольного текста и не смог бы собрать из него строку ленты.
 */
export function BookingPaymentDialog({
  bookingId,
  bookingCode,
  open,
  onOpenChange,
}: {
  bookingId: string
  bookingCode: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const record = useRecordBookingPayment(bookingId, `Платёж по брони ${bookingCode}`)

  const [amount, setAmount] = useState('')
  const [kindValue, setKindValue] = useState<string>(FALLBACK_KIND.value)
  const [invoiceName, setInvoiceName] = useState('')
  const [note, setNote] = useState('')

  /*
   * ⚠️ КЛЮЧ ИДЕМПОТЕНТНОСТИ МНЁТСЯ ОДИН РАЗ НА ОКНО, а не на вызов. Он принадлежит ДЕЙСТВИЮ —
   * одному «Записать» вместе со всеми повторами после сбоя сети. Мни его при отправке, и повтор
   * после «сервер не ответил» записал бы ВТОРОЙ платёж живыми деньгами: клиент не отличает
   * «не дошло» от «дошло, записали, ответ потерялся». Ровно та же конструкция, что у формы
   * создания брони; полный разбор — в `shared/api/idempotency.ts`.
   */
  const [idempotencyKey] = useState(newIdempotencyKey)

  const kopecks = rubleInputToAmount(amount)
  const chosen = KINDS.find((entry) => entry.value === kindValue) ?? FALLBACK_KIND
  // Ноль домен принимает, но записывать нулевой платёж бессмысленно: это не событие.
  const valid = kopecks !== undefined && kopecks > 0
  // Исход первого нажатия неизвестен, или по ключу записано другое: тот же ключ ответит тем же.
  // Выход — проверить ленту и открыть окно заново, уже с новым ключом.
  const failure = record.isError ? paymentFailure(record.error) : null
  const stuck = failure?.stuck === true

  const save = () => {
    if (!valid) return
    record.mutate(
      {
        body: {
          amount: kopecks,
          kind: chosen.kind,
          direction: chosen.direction,
          invoiceName: invoiceName.trim(),
          note: note.trim(),
        },
        idempotencyKey,
      },
      { onSuccess: () => onOpenChange(false) },
    )
  }

  return (
    <Dialog
      open={open}
      /*
       * Пока запрос летит, окно не закрывается: отказ показывает оно само, рядом с набранным. Тот же
       * разбор, что у окна правки брони.
       */
      onOpenChange={(next) => {
        if (!next && record.isPending) return
        onOpenChange(next)
      }}
      icon="ruble"
      title="Записать платёж"
      description="То, что приняли или вернули вы сами. Деньги, о которых сообщила площадка, живут в её данных и правке не подлежат."
      width={460}
    >
      <div className={s.body}>
        <label className={s.field}>
          <span className={s.label}>Сумма, ₽</span>
          <Input
            className={s.money}
            inputMode="decimal"
            placeholder="0"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </label>

        <label className={s.field}>
          <span className={s.label}>Что записываем</span>
          <Select
            options={KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
            value={kindValue}
            onChange={(event) => setKindValue(event.target.value)}
          />
        </label>

        <label className={s.field}>
          <span className={s.label}>Чем платили</span>
          <Input
            placeholder="Наличные"
            value={invoiceName}
            onChange={(event) => setInvoiceName(event.target.value)}
          />
          {/*
            Не выпадающий список: у домена это свободная строка, и закрыть её нашими четырьмя
            словами значило бы запретить пятое — «Перевод на счёт», «СБП», имя конкретной кассы.
          */}
          <span className={s.hint}>Своими словами: «Наличные», «Карта», «Перевод на счёт».</span>
        </label>

        <label className={s.field}>
          <span className={s.label}>Примечание</span>
          <Textarea
            rows={2}
            placeholder="Например: на заселении, от второго гостя"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>

        {failure && (
          <p className={s.error} role="alert">
            {failure.title}
            {/* Пояснение — что делать: у ответа ключа идемпотентности весь смысл в нём. */}
            {failure.advice ? (
              <>
                <br />
                {failure.advice}
              </>
            ) : null}
          </p>
        )}
      </div>

      <div className={s.footer}>
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={record.isPending}>
          Отмена
        </Button>
        <Button onClick={save} disabled={!valid || record.isPending || stuck}>
          {record.isPending ? 'Записываем…' : 'Записать'}
        </Button>
      </div>
    </Dialog>
  )
}
